'use client';

import type { Swiper as SwiperCore } from 'swiper';
import type { HomeLogPageNameValue } from '@/app/(pages)/sub-home/[category]/constants';
import type { CategoryFilteredTicketRankingItemData } from '@weberus/shared-types';
import { type FC, useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useAtomValue } from 'jotai';
import { chunk } from 'lodash-es';
import { YaTrackerImpression } from '@weberus/client-tracker';
import { VerticalTicketItem } from '@weberus/ui/components/vertical-product-ticket-item';
import { waitUntilClickTimeout } from '@weberus/utils';
import { clientLogProtobuf } from '@weberus/client-tracker/utils';
import { pcWidthAtom } from '@/app/_shared/store/device';
import { navigateTo } from '@/app/_shared/utils/action-navigator';
import { HomePageLog } from '@/app/_shared/protobuf-log/clientlogs';
import { cn } from '@/app/_shared/utils/tw-cn';
import { SwiperNavButton } from '../../common';

export interface TicketProductAreaProps {
  pageNameLog: HomeLogPageNameValue;
  widgetLogMeta: Record<string, unknown>;
  tabItems: CategoryFilteredTicketRankingItemData[][];
  tabLogMeta: Record<string, unknown>;
  setSelectedIndex: (index: number) => void;
  swiperRef: React.MutableRefObject<SwiperCore | null>;
}

export const TicketProductArea: FC<TicketProductAreaProps> = ({ tabItems, setSelectedIndex, swiperRef }) => {
  const { isPcWidth } = useAtomValue(pcWidthAtom);

  // 각 탭의 아이템들을 5개씩 청크로 나누기
  const allSlides = useMemo(() => {
    const slides: {
      items: CategoryFilteredTicketRankingItemData[];
      tabIndex: number;
      chunkIndex: number;
    }[] = [];

    tabItems.forEach((items, tabIndex) => {
      const itemsPerSlide = isPcWidth ? 5 : 1;
      const chunks = chunk(items, itemsPerSlide); // lodash chunk 사용

      chunks.forEach((itemChunk, chunkIndex) => {
        slides.push({
          items: itemChunk,
          tabIndex,
          chunkIndex,
        });
      });
    });

    return slides;
  }, [tabItems, isPcWidth]);

  const showNavButton = allSlides.length > 1;

  const handleItemImpression = (item: CategoryFilteredTicketRankingItemData) => {
    clientLogProtobuf(HomePageLog.WidgetTabTitleCardId.impression, { logMeta: item.serverLogMeta })();
  };

  const handleClickTicketItem = async (
    e: React.MouseEvent<HTMLAnchorElement>,
    item: CategoryFilteredTicketRankingItemData,
  ) => {
    e.preventDefault();
    await waitUntilClickTimeout(HomePageLog.WidgetTabTitleCardId.click({ logMeta: item.serverLogMeta }));
    item.action && navigateTo(item.action);
  };

  return (
    <div className="relative pc:w-pc-size">
      {/* mw gap 을 넣게되면 슬라이드 시 맨 왼쪽 여백이 없게됨, ml-8 로 element에서 처리 */}
      <Swiper
        loop={isPcWidth && allSlides.length > 1}
        slidesPerView="auto"
        spaceBetween={isPcWidth ? 20 : 0}
        onRealIndexChange={(swiper) => {
          const currentSlideIndex = swiper.realIndex;
          const currentSlide = allSlides[currentSlideIndex];

          if (currentSlide) {
            // 현재 슬라이드의 탭 인덱스로 상위 컴포넌트의 selectedIndex 업데이트
            setSelectedIndex(currentSlide.tabIndex);
          }
        }}
        onSwiper={(swiper: SwiperCore) => {
          swiperRef.current = swiper;
        }}>
        {isPcWidth ? (
          <>
            {allSlides.map((slide, slideIndex) => (
              <SwiperSlide key={`pc_${slide.tabIndex}_${slide.chunkIndex}_${slideIndex}`} className="!w-fit">
                <div className="flex gap-20">
                  {slide.items.map((item, itemIndex) => (
                    <div key={`pc_item_${slide.tabIndex}_${slide.chunkIndex}_${itemIndex}`} className="w-[240px]">
                      <YaTrackerImpression onImpressed={() => handleItemImpression(item)}>
                        <VerticalTicketItem {...item} onClick={(e) => handleClickTicketItem(e, item)} />
                      </YaTrackerImpression>
                    </div>
                  ))}
                </div>
              </SwiperSlide>
            ))}
          </>
        ) : (
          <>
            {allSlides.map((slide, slideIndex) => (
              <SwiperSlide
                key={`${slide.tabIndex}_${slide.chunkIndex}_${slideIndex}`}
                className={cn('ml-8 !w-fit [&:last-child]:mr-16', slide.chunkIndex === 0 ? 'ml-16' : '')}>
                <div className="flex w-[160px] pc:w-[240px] pc:gap-20 pc:space-y-0">
                  {slide.items.map((item, itemIndex) => (
                    <YaTrackerImpression
                      key={`${slide.tabIndex}_${slide.chunkIndex}_${itemIndex}`}
                      onImpressed={() => handleItemImpression(item)}>
                      <VerticalTicketItem {...item} onClick={(e) => handleClickTicketItem(e, item)} />
                    </YaTrackerImpression>
                  ))}
                </div>
              </SwiperSlide>
            ))}
          </>
        )}
      </Swiper>

      {!!showNavButton && <SwiperNavButton swiperRef={swiperRef} top={136} />}
    </div>
  );
};
